import { useMemo, useState } from 'react';
import { Link } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Edit, Plus, ScrollText, Search } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  CHANGE_IMPACT, formatDateTime, listPolicies, policyKeys, SCOPE_LABELS, type PolicyListItem, type PolicyScope,
} from '@/lib/policies';

const ALL_SCOPES = 'all';

function linkedName(policy: PolicyListItem) {
  if (policy.policyScope === 'feature_key') return policy.featureKeyName ?? policy.featureKeyId ?? '';
  if (policy.policyScope === 'feature_group') return policy.featureGroupName ?? policy.featureGroupId ?? '';
  return '';
}

export default function PoliciesPage() {
  const [search, setSearch] = useState('');
  const [scope, setScope] = useState<PolicyScope | typeof ALL_SCOPES>(ALL_SCOPES);
  const { data: policies, isLoading, error } = useQuery({ queryKey: policyKeys.all, queryFn: listPolicies });

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (policies ?? [])
      .filter(p => scope === ALL_SCOPES || p.policyScope === scope)
      .filter(p => !term || [p.title, p.id, p.policyType, linkedName(p)].some(v => v?.toLowerCase().includes(term)))
      .sort((a, b) => a.title.localeCompare(b.title));
  }, [policies, search, scope]);

  return (
    <div className="flex-1 p-8">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <ScrollText className="h-6 w-6" /> Policies
            </h1>
            <p className="text-slate-600 mt-1">Terms, privacy and data-sharing policies users accept in the app</p>
          </div>
          <Link href="/policies/add">
            <Button><Plus className="h-4 w-4 mr-2" /> New policy</Button>
          </Link>
        </div>

        <Card>
          <CardContent className="pt-6 flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-64">
              <Search className="h-4 w-4 absolute left-3 top-3 text-slate-400" />
              <Input placeholder="Search by title, ID, type or feature" value={search} onChange={e => setSearch(e.target.value)} className="pl-10" />
            </div>
            <Select value={scope} onValueChange={v => setScope(v as PolicyScope | typeof ALL_SCOPES)}>
              <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_SCOPES}>All scopes</SelectItem>
                {(Object.keys(SCOPE_LABELS) as PolicyScope[]).map(s => <SelectItem key={s} value={s}>{SCOPE_LABELS[s]}</SelectItem>)}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-lg">Policies ({filtered.length})</CardTitle></CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-3">{[1, 2, 3].map(i => <Skeleton key={i} className="h-10 w-full" />)}</div>
            ) : error ? (
              <p className="py-8 text-center text-red-600">Could not load policies: {(error as Error).message}</p>
            ) : filtered.length === 0 ? (
              <p className="py-8 text-center text-slate-500">
                {policies?.length ? 'No policies match your search.' : "No policies yet. Click 'New policy' to create one."}
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Policy</TableHead>
                    <TableHead>Scope</TableHead>
                    <TableHead>Acceptance</TableHead>
                    <TableHead>Version</TableHead>
                    <TableHead>Published</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map(policy => (
                    <TableRow key={policy.id}>
                      <TableCell>
                        <div className="font-medium">{policy.title || 'Untitled'}</div>
                        <div className="font-mono text-xs text-slate-500">{policy.id}</div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{SCOPE_LABELS[policy.policyScope]}</Badge>
                        {linkedName(policy) && <div className="text-xs text-slate-500 mt-1">{linkedName(policy)}</div>}
                      </TableCell>
                      <TableCell>
                        {policy.isRequired ? <Badge>Required</Badge> : <Badge variant="secondary">Optional</Badge>}
                      </TableCell>
                      <TableCell>{policy.published ? policy.currentVersion : '—'}</TableCell>
                      <TableCell className="whitespace-nowrap">{policy.published ? formatDateTime(policy.publishedAt) : '—'}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap items-center gap-1">
                          {!policy.published && <Badge variant="secondary">Not published</Badge>}
                          {policy.draft && (
                            <Badge variant="outline" className="border-amber-400 text-amber-800">
                              Draft{policy.draft.bump ? ` · ${CHANGE_IMPACT[policy.draft.bump].label.toLowerCase()}` : ''} → {policy.draft.nextVersion}
                            </Badge>
                          )}
                          {policy.issues.length > 0 && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="inline-flex items-center gap-1 text-xs text-red-700">
                                  <AlertTriangle className="h-4 w-4" /> {policy.issues.length} issue{policy.issues.length > 1 ? 's' : ''}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-sm">
                                <ul className="list-disc pl-4">{policy.issues.map(i => <li key={i}>{i}</li>)}</ul>
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Link href={`/policies/edit/${policy.id}`}>
                          <Button variant="outline" size="sm" title="Open"><Edit className="h-4 w-4" /></Button>
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
